import { motion } from "framer-motion";
import { Lock, Shield, Eye, Database, Mail } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

export default function PrivacyPolicyPage() {
  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <Lock className="w-20 h-20 mx-auto mb-6" />
            <h1 className="text-6xl font-black mb-6">Privacy Policy</h1>
            <p className="text-2xl text-gray-300 max-w-3xl mx-auto">
              Your privacy and data security are our top priorities
            </p>
            <p className="text-sm text-gray-400 mt-4">Last Updated: January 2025</p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="space-y-8"
        >
          {/* Introduction */}
          <Card className="border-2 border-gray-200 shadow-xl">
            <CardContent className="p-8">
              <h2 className="text-3xl font-black text-gray-900 mb-4">Introduction</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                LCTX ("we," "our," or "us") is committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or purchase our products.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                By using our website, you agree to the collection and use of information in accordance with this policy.
              </p>
            </CardContent>
          </Card>

          {/* Information We Collect */}
          <Card className="border-2 border-blue-200 shadow-xl">
            <CardContent className="p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Database className="w-8 h-8 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-3xl font-black text-gray-900 mb-4">Information We Collect</h2>
                  
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">Personal Information</h3>
                      <p className="text-gray-700 mb-3">When you make a purchase or contact us, we may collect:</p>
                      <ul className="space-y-2">
                        {[
                          "Name and contact details (email, phone number)",
                          "Shipping and billing addresses",
                          "Payment information (processed securely via Stripe/PayPal)",
                          "Order history and preferences"
                        ].map((item, index) => (
                          <li key={index} className="flex items-start gap-2 text-gray-700">
                            <span className="text-blue-600 font-bold">•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">Automatically Collected Information</h3>
                      <p className="text-gray-700 mb-3">When you visit our website, we automatically collect:</p>
                      <ul className="space-y-2">
                        {[
                          "IP address and browser type",
                          "Device information",
                          "Pages visited and time spent",
                          "Referring website",
                          "Cookies and tracking technologies"
                        ].map((item, index) => (
                          <li key={index} className="flex items-start gap-2 text-gray-700">
                            <span className="text-blue-600 font-bold">•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* How We Use Your Information */}
          <Card className="border-2 border-green-200 shadow-xl">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Eye className="w-8 h-8 text-green-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-3xl font-black text-gray-900 mb-4">How We Use Your Information</h2>
                  <p className="text-gray-700 mb-4">We use the information we collect to:</p>
                  <div className="space-y-3">
                    {[
                      "Process and fulfill your orders",
                      "Send order confirmations and shipping updates",
                      "Provide customer support",
                      "Improve our website and services",
                      "Send marketing communications (with your consent)",
                      "Detect and prevent fraud",
                      "Comply with legal obligations"
                    ].map((item, index) => (
                      <div key={index} className="flex items-start gap-3 bg-green-50 rounded-lg p-3">
                        <span className="text-green-600 font-bold text-lg">✓</span>
                        <span className="text-gray-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Data Sharing */}
          <Card className="border-2 border-purple-200 shadow-xl">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield className="w-8 h-8 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-3xl font-black text-gray-900 mb-4">Information Sharing & Disclosure</h2>
                  <p className="text-gray-700 mb-4">We may share your information with:</p>
                  <div className="space-y-4">
                    <div className="bg-purple-50 rounded-lg p-4">
                      <h3 className="font-bold text-purple-900 mb-2">Service Providers</h3>
                      <p className="text-gray-700">Payment processors (Stripe, PayPal), shipping companies, and email services that help us operate our business.</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <h3 className="font-bold text-purple-900 mb-2">Legal Requirements</h3>
                      <p className="text-gray-700">When required by law, court order, or government regulations.</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <h3 className="font-bold text-purple-900 mb-2">Business Transfers</h3>
                      <p className="text-gray-700">In connection with a merger, acquisition, or sale of assets.</p>
                    </div>
                  </div>
                  <p className="text-gray-700 mt-4 font-semibold">
                    We will NEVER sell your personal information to third parties for marketing purposes.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Data Security */}
          <Card className="border-2 border-orange-200 shadow-xl">
            <CardContent className="p-8">
              <h2 className="text-3xl font-black text-gray-900 mb-4">🔒 Data Security</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.
              </p>
              <div className="bg-orange-50 rounded-lg p-4 space-y-2">
                <p className="text-gray-700 flex items-start gap-2">
                  <span className="text-orange-600 font-bold">•</span>
                  <span>SSL encryption for data transmission</span>
                </p>
                <p className="text-gray-700 flex items-start gap-2">
                  <span className="text-orange-600 font-bold">•</span>
                  <span>Secure payment processing via Stripe and PayPal</span>
                </p>
                <p className="text-gray-700 flex items-start gap-2">
                  <span className="text-orange-600 font-bold">•</span>
                  <span>Regular security audits and updates</span>
                </p>
                <p className="text-gray-700 flex items-start gap-2">
                  <span className="text-orange-600 font-bold">•</span>
                  <span>Limited employee access to personal data</span>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Your Rights */}
          <Card className="border-2 border-red-200 shadow-xl">
            <CardContent className="p-8">
              <h2 className="text-3xl font-black text-gray-900 mb-4">Your Privacy Rights</h2>
              <p className="text-gray-700 mb-4">Under applicable privacy laws in the UAE and Saudi Arabia, you have the right to:</p>
              <div className="space-y-3">
                {[
                  "Access your personal information",
                  "Correct inaccurate or incomplete data",
                  "Request deletion of your data",
                  "Object to processing of your data",
                  "Opt-out of marketing communications",
                  "Request data portability"
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3 bg-red-50 rounded-lg p-3">
                    <span className="text-red-600 font-bold text-lg">→</span>
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-gray-700 mt-4">
                To exercise these rights, contact us at <strong>hkjenholtd@gmail.com</strong>
              </p>
            </CardContent>
          </Card>

          {/* Cookies */}
          <Card className="border-2 border-yellow-200 shadow-xl">
            <CardContent className="p-8">
              <h2 className="text-3xl font-black text-gray-900 mb-4">🍪 Cookies & Tracking</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                We use cookies and similar tracking technologies to enhance your browsing experience, analyze website traffic, and personalize content.
              </p>
              <p className="text-gray-700 mb-4">You can control cookies through your browser settings. Note that disabling cookies may affect website functionality.</p>
              <div className="bg-yellow-50 rounded-lg p-4">
                <p className="text-gray-700 font-semibold">We use:</p>
                <ul className="mt-2 space-y-1 text-gray-700">
                  <li>• Essential cookies (required for website operation)</li>
                  <li>• Analytics cookies (Google Analytics)</li>
                  <li>• Marketing cookies (with your consent)</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card className="bg-gradient-to-r from-gray-900 to-black text-white shadow-xl">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <h2 className="text-3xl font-black mb-4">Contact Us</h2>
                  <p className="text-xl text-gray-300 mb-4">
                    If you have any questions about this Privacy Policy or how we handle your data:
                  </p>
                  <div className="space-y-2">
                    <p className="text-lg">
                      📧 Email: <strong>hkjenholtd@gmail.com</strong>
                    </p>
                    <p className="text-lg">
                      📞 Phone: <strong>+852-63613177</strong>
                    </p>
                    <p className="text-lg">
                      📍 Address: Saih Shuaib 3, Dubai Industrial City, Dubai, UAE
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Updates */}
          <Card className="bg-blue-50 border-2 border-blue-300 shadow-xl">
            <CardContent className="p-8">
              <h2 className="text-3xl font-black text-gray-900 mb-4">Policy Updates</h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated "Last Updated" date. We encourage you to review this policy periodically.
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}